import React, { useState, useMemo, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
//import WarehouseSelectEditor from "./WarehouseSelectEditor";
// import "ag-grid-enterprise/styles/ag-grid.css";
// import "ag-grid-enterprise/styles/ag-theme-alpine.css";
// import WarehouseCellEditor from "./WarehouseCellEditor";
import { IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../axiosConfig";
import dayjs from "dayjs";


function roundHalfUp(value) {
  return Math.round(value);
}

function calculateDiscountSpend(data) {
  if (!data.currentQty || !data.currentRate || !data.rateImpact || data.rateImpact === 0 ||  data.quantityImpact === 0) return 0;
  const qtyImpact = (data.currentQty * data.quantityImpact) / 100;
  const totalQty = roundHalfUp(qtyImpact + data.currentQty);
  const adjustedRate =roundHalfUp(data.currentRate * (data.rateImpact / 100));
  //console.log(totalQty)
  const finalValue = roundHalfUp(adjustedRate * totalQty);
  return finalValue.toFixed(2);
}
function calculateIncrementRev(data) {
  if (!data.currentQty || !data.currentRate || !data.quantityImpact || !data.rateImpact || data.rateImpact === 0 ||  data.quantityImpact === 0) return 0;
  const newRate = roundHalfUp(data.currentRate * (1 - data.rateImpact / 100));
  const qtyImpact = roundHalfUp(data.currentQty * (data.quantityImpact / 100));
  const adjustedQty = data.currentQty + qtyImpact;
  const impactedAmount = newRate * adjustedQty;
  const baseAmount = roundHalfUp(data.currentRate * data.currentQty);
  const difference = roundHalfUp(impactedAmount - baseAmount);
  return difference.toFixed(2);
}
function calculateRevGrowth(data) {
  if (!data.currentRev || !data.rateImpact || data.rateImpact === 0 ||  data.quantityImpact === 0) return 0;
  const incrementRev = calculateIncrementRev(data);
  if (!incrementRev) return 0;
  return ((incrementRev / data.currentRev) * 100).toFixed(2);
}

const SchemeDetails = ({ warehouseData = [], onWarehouseDetailsChange, onDeleteScheme }) => {
  // Use warehouseData as the source of truth for the grid
  const [rowData, setRowData] = useState([]);

  useEffect(() => {
    // When warehouseData changes (from API), update the grid rows
    setRowData(
      (warehouseData || []).map(w => ({
        ...w,
        rateImpact: typeof w.rateImpact === 'number' ? w.rateImpact : 0,
        quantityImpact: typeof w.quantityImpact === 'number' ? w.quantityImpact : 0,
      }))
    );
  }, [warehouseData]);

  // Custom cell renderer for delete button
  const DeleteButtonRenderer = (props) => {
    if (props.data.scheme_present === 1) {
      return (
        <Tooltip title="Delete Scheme">
          <IconButton
            size="small"
            onClick={() => onDeleteScheme && onDeleteScheme(props.data)}
            sx={{ color: "#d32f2f" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    return null;
  };

  // Custom cell renderer for conditional styling
  const WarehouseCellRenderer = (props) => {
    const isSchemePresent = props.data.scheme_present === 1;
    return (
      <div style={{
        backgroundColor: isSchemePresent ? "#808080" : "transparent",
        fontWeight: isSchemePresent ? "bold" : "normal",
        padding: "8px",
        borderRadius: "4px",
        border: isSchemePresent ? "1px solid #ff9800" : "none"
      }}>
        {props.value}
      </div>
    );
  };

  const columnDefs = useMemo(() => [
    { 
      headerName: "Warehouse", 
      field: "Warehouse",
      cellRenderer: WarehouseCellRenderer,
      width: 120,
      editable: false
    },
    { headerName: "Current MRP", field: "MRP", hide: true },
    { 
      headerName: "Current Rate", 
      field: "currentRate",
      cellRenderer: WarehouseCellRenderer,
      width: 150,
      editable: false
      
    },
    { 
      headerName: "Current Rev", 
      field: "currentRev",
      cellRenderer: WarehouseCellRenderer,
      width: 150,
       editable: false
      
    },
    { 
      headerName: "Current Qty", 
      field: "currentQty",
      cellRenderer: WarehouseCellRenderer,
      width: 150,
       editable: false
    },
    {
      headerName: "Rate Impact (%)",
      field: "rateImpact",
      editable: true,
      cellRenderer: WarehouseCellRenderer,
      valueSetter: (params) => {
        let newValue = Number(params.newValue);
        if (isNaN(newValue) || newValue < 0) newValue = 0;
        params.data.rateImpact = newValue;
        return true;
      },
      width: 150
    },
    {
      headerName: "Qty Impact (%)",
      field: "quantityImpact",
      editable: true,
      cellRenderer: WarehouseCellRenderer,
      valueSetter: (params) => {
        let newValue = Number(params.newValue);
        if (isNaN(newValue) || newValue < 0) newValue = 0;
        params.data.quantityImpact = newValue;
        return true;
      },
      width: 150
    },
    { 
      headerName: "Discount Spend", 
      valueGetter: (params) => calculateDiscountSpend(params.data),
      cellRenderer: WarehouseCellRenderer,
      width: 150
    },
    { 
      headerName: "Increment Rev", 
      valueGetter: (params) => calculateIncrementRev(params.data),
      cellRenderer: WarehouseCellRenderer,
      width: 150
    },
    { 
      headerName: "Rev Growth (%)", 
      valueGetter: (params) => calculateRevGrowth(params.data),
      cellRenderer: WarehouseCellRenderer,
      width: 150
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: DeleteButtonRenderer,
      width: 80,
      sortable: false,
      filter: false,
      editable: false
    }
  ], []);

  const onCellValueChanged = (params) => {
    const updatedRows = rowData.map((row, idx) =>
      idx === params.node.rowIndex ? { ...row, ...params.data } : row
    );
    setRowData(updatedRows);
    if (onWarehouseDetailsChange) {
      onWarehouseDetailsChange(updatedRows);
    }
  };

  return (
    <div className="ag-theme-alpine" style={{ height: 400, width: "100%" }}>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={{
          resizable: true,
          sortable: true,
          editable: true,
        }}
        stopEditingWhenCellsLoseFocus
        onCellValueChanged={onCellValueChanged}
      />
    </div>
  );
};

export default SchemeDetails;
